import React from "react";
import { motion } from "framer-motion";
import {
  Compass,
  FileCheck,
  Code2,
  TrendingUp,
  Handshake,
  LineChart,
  GraduationCap,
} from "lucide-react";

const services = [
  {
    title: "Strategy & advisory",
    desc: "Sit with us for an hour, leave with a plan. We help founders make the next three decisions, not the next thirty.",
    Icon: Compass,
    num: "01",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=70",
  },
  {
    title: "Compliance & filings",
    desc: "GST, income tax, ROC, labour — filed on time, notices handled. Monthly retainers or one-off fixes.",
    Icon: FileCheck,
    num: "02",
    image:
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=70",
  },
  {
    title: "Tech & product",
    desc: "Websites, apps, CRMs and automations that remove 20 hours from someone's week — built to last.",
    Icon: Code2,
    num: "03",
    image:
      "https://images.unsplash.com/photo-1517180102446-f3ece451e9d8?auto=format&fit=crop&w=900&q=70",
  },
  {
    title: "Growth & marketing",
    desc: "Performance ads, local campaigns, content and SEO. We measure what matters — the rest we stop doing.",
    Icon: TrendingUp,
    num: "04",
    image:
      "https://images.unsplash.com/photo-1533750516457-a7f992034fec?auto=format&fit=crop&w=900&q=70",
  },
  {
    title: "Real estate support",
    desc: "Sourcing, due-diligence and honest deal-making. We sit on your side of the table, not the broker's.",
    Icon: Handshake,
    num: "05",
    image:
      "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=900&q=70",
  },
  {
    title: "Finance & capital",
    desc: "Books closed monthly, forecasts you can defend, and investor conversations we'll walk into with you.",
    Icon: LineChart,
    num: "06",
    image:
      "https://images.unsplash.com/photo-1543286386-713bdd548da4?auto=format&fit=crop&w=900&q=70",
  },
  {
    title: "Education setup",
    desc: "From licensing your institute to launching your first online batch — we've shipped dozens of these.",
    Icon: GraduationCap,
    num: "07",
    image:
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=900&q=70",
  },
];

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-carousel"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between gap-6 mb-12"
        >
          <div>
            <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#0A82D8]">
              Services
            </span>
            <h2 className="mt-3 font-display font-semibold text-4xl md:text-5xl lg:text-[3.25rem] tracking-tighter leading-[1.05]">
              What you actually get <br className="hidden sm:block" />
              <span className="text-gradient-cyan">when you hire us.</span>
            </h2>
          </div>
          <div className="hidden md:block text-xs text-muted-foreground tracking-wider uppercase">
            Swipe / scroll →
          </div>
        </motion.div>

        <div
          className="flex gap-5 overflow-x-auto overflow-y-hidden scrollbar-hide snap-x snap-mandatory pb-4 -mx-6 px-6 md:-mx-10 md:px-10"
          style={{ scrollPaddingLeft: "1.5rem" }}
        >
          {services.map((s, i) => {
            const { Icon } = s;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                data-testid={`service-card-${i}`}
                className="snap-start relative group flex-shrink-0 w-[80vw] sm:w-[44vw] md:w-[34vw] lg:w-[26vw] h-[440px] rounded-3xl overflow-hidden glass-strong flex flex-col"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(10,130,216,0.25) 0%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.9) 100%)",
                    }}
                  />
                  <div className="dark:hidden absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background to-transparent" />
                  <div className="hidden dark:block absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[hsl(var(--card))] to-transparent" />
                  <div className="absolute top-4 left-4 h-11 w-11 rounded-xl glass flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="absolute top-4 right-4 font-display font-bold text-xl text-white/90 drop-shadow">
                    {s.num}
                  </span>
                </div>
                <div className="flex-1 flex flex-col px-6 pb-6 -mt-1">
                  <h3 className="font-display font-semibold text-xl md:text-[1.4rem] tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {s.desc}
                  </p>
                  <div className="mt-auto pt-5 h-px w-10 bg-gradient-to-r from-[#00B4F0] to-[#0A82D8] group-hover:w-20 transition-all duration-500" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
